import { SCENE_KIND_NOUN, sceneVisualSchema } from '@platform/schema/content';

import { ANSWER_LABELS, answerFields, keyedAnswers } from './answers';

import type { Step } from './history';
import type { UpdateSceneData } from '@platform/client';
import type { ArcChange } from '@platform/schema/chat';
import type { PersonaLabel, SceneVisual } from '@platform/schema/content';
import type { LessonWorkspace } from '@platform/schema/review';

export type ReviewScene = LessonWorkspace['scenes'][number];

/**
 * What one edit sends. Taken from the SDK's own request body rather than restated, so a
 * field the API stops accepting stops compiling here — GEN-49 widened it from prompt and
 * labels to the answer keys, and a hand-kept copy would have been the third place to
 * remember.
 */
export type ScenePatch = UpdateSceneData['body'];
export interface ReviewItem {
  id: string;
  label: string;
  feedback?: string | undefined;
}

/**
 * The labelled items on a scene, whichever key its kind puts them under.
 *
 * `CHOICE` calls them `options`, `ALLOCATE` calls them `categories`, `RANK` and `BUCKET`
 * call them `items` — a difference the *learner's* renderer cares about and an editor does
 * not. Reading all three keeps the switch out of the form, which would otherwise grow an
 * arm every time a scene kind is added.
 *
 * `keyedAnswers` is that switch since GEN-77, which needed the same walk to read what keys
 * each item. Two copies of "which key does this mechanic call its items" is one place for
 * the twelfth kind to be forgotten; the narrower return type is what callers here want.
 */
export function editableItems(scene: ReviewScene): ReviewItem[] {
  return keyedAnswers(scene);
}

const NOUN: Record<string, string> = SCENE_KIND_NOUN;

/**
 * What to call this kind on screen. Widened because the SDK renders `kind` as a plain
 * string, and unknown kinds read raw.
 */
export const sceneKindName = (kind: string): string => NOUN[kind] ?? kind;

/**
 * The subheadline, kept while it is switched off.
 *
 * `''` and not `undefined` when the column is empty, because a scene written without one
 * is the one somebody most wants to add one to — an editor that only appears once the
 * field is already filled cannot fill it.
 */
export const sceneBody = (scene: ReviewScene): string => scene.body ?? '';

/** The scene's visual, parsed: the SDK widens its union to `kind: string`. */
export const sceneVisualOf = (scene: ReviewScene): SceneVisual | undefined =>
  'visual' in scene
    ? sceneVisualSchema.safeParse(scene.visual).data
    : undefined;

/** The hint's text, kept while it is switched off. `''` so the editor can fill it. */
export const sceneHint = (scene: ReviewScene): string => scene.hint ?? '';

/** The name every field save is recorded on the action feed under. */
export const SAVE = 'Save';

/** And the name a change to the scenes themselves is recorded under — GEN-45. */
export const CHANGE_THE_SCENES = 'Change the scenes';

/**
 * What one edit sends, whichever of the three rows a field belongs to — GEN-43.
 *
 * `lessonId` on every arm since GEN-70, when a conversation came to hold several lessons
 * and one undo stack came to span them. A scene id and a question id are unique on their
 * own, so it is not what finds the row — it is what says which lesson's payload the
 * previous value is read from, and every producer of a write already knows it. Searching
 * for it instead was a scan over every scene of every lesson, and one that degraded to
 * "not undoable" when it missed.
 */
interface Lesson {
  kind: 'lesson';
  lessonId: string;
  body: {
    title?: string;
    summary?: string;
    personas?: PersonaLabel[];
    tags?: string[];
  };
}

export type LessonWrite =
  | {
      kind: 'lesson';
      lessonId: string;
      body: {
        title?: string;
        summary?: string;
        personas?: PersonaLabel[];
        tags?: string[];
      };
    }
  | { kind: 'scene'; lessonId: string; sceneId: string; body: ScenePatch }
  | {
      kind: 'question';
      lessonId: string;
      questionId: string;
      explanation: string;
    }
  /**
   * Which scenes the lesson has, and in what order — GEN-77.
   *
   * The one arm that is not a `PATCH` body, and the one `undoStep` returns nothing for.
   * Both follow from the same fact: a scene list is replaced wholesale, and `placeScenes`
   * has never recorded a step for the Add, Remove and drag controls either. An applied arc
   * proposal is therefore exactly as reversible as the button beside it — no more, and no
   * less. `docs/decisions/planner-proposals.md`.
   */
  | { kind: 'arc'; lessonId: string; change: ArcChange };

/**
 * A write to a row's fields — everything but the arc.
 *
 * The undo stack is over these and not over `LessonWrite`, which is what keeps the two
 * facts about the arc from having to be restated: it is not a `PATCH`, and it has no way
 * back. Both are the one line above.
 */
export type FieldWrite = Exclude<LessonWrite, { kind: 'arc' }>;

/**
 * The patch that puts a scene back the way it was.
 *
 * Only the keys the patch carries are read back. A patch is partial, and inverting the
 * whole scene would send columns the reviewer did not touch.
 */
function inverseScene(scene: ReviewScene, patch: ScenePatch): ScenePatch {
  const before: ScenePatch = {};

  if (patch.prompt !== undefined) before.prompt = scene.prompt;
  if (patch.body !== undefined) before.body = sceneBody(scene);
  if (patch.bodyEnabled !== undefined) before.bodyEnabled = scene.bodyEnabled;
  if (patch.hint !== undefined) before.hint = sceneHint(scene);
  if (patch.hintEnabled !== undefined) before.hintEnabled = scene.hintEnabled;
  if (patch.visual !== undefined) before.visual = sceneVisualOf(scene) ?? null;

  if (patch.items !== undefined) {
    const was = new Map(
      editableItems(scene).map((item) => [item.id, item.label]),
    );

    before.items = patch.items.map((one) => ({
      id: one.id,
      label: was.get(one.id) ?? '',
    }));
  }

  /**
   * The way back out of a re-key, column by column — widened past `correctAmount` by
   * GEN-77.
   *
   * Only the columns the key actually sent are put back, for the same reason the patch
   * itself is partial: a CHOICE re-keyed on `isCorrect` has no `correctPosition` to
   * restore, and sending one would key it on a column its mechanic does not read.
   */
  if (patch.keys !== undefined) {
    const was = new Map(keyedAnswers(scene).map((one) => [one.id, one]));

    before.keys = patch.keys.map((one) => {
      const held = was.get(one.id);

      return {
        id: one.id,
        ...(one.isCorrect === undefined
          ? {}
          : { isCorrect: held?.isCorrect ?? false }),
        ...(one.correctPosition === undefined
          ? {}
          : { correctPosition: held?.correctPosition ?? 1 }),
        ...(one.correctAmount === undefined
          ? {}
          : { correctAmount: held?.correctAmount ?? 0 }),
      };
    });
  }

  /**
   * An answer field the scene did not have yet is left out rather than inverted — GEN-78.
   *
   * A patch's absent key means "leave this alone", so there is no value that puts a null
   * column back to null: typing the first `targetMin` into a freshly switched Slider is a
   * write with no inverse to send. `undoStep` drops a step that inverts to nothing, which
   * is the same honest outcome it already gives when the row itself cannot be read.
   */
  for (const answer of answerFields(scene))
    if (patch[answer.key] !== undefined && answer.value !== undefined)
      before[answer.key] = answer.value;

  return before;
}

/**
 * What a reviewer calls the column a patch touches. Every patch here carries one.
 *
 * Exported because GEN-66 shows the same names *before* a write as this names them after:
 * the chat's proposal diff labels its rows from here, so the "Field" column the operator
 * reads and the "Undo Scene 2 Headline" button they press afterwards cannot drift apart.
 */
export const FIELD_NAMES: Record<string, string> = {
  title: 'Title',
  summary: 'Summary',
  personas: 'Personas',
  tags: 'Tags',
  prompt: 'Headline',
  body: 'Subheadline',
  bodyEnabled: 'Subheadline on or off',
  hint: 'Hint',
  hintEnabled: 'Hint on or off',
  items: 'Item',
  // Not 'Amount' since GEN-77: a re-key carries which option is correct and which order is
  // right as well, and the row named "Scene 2 Amount" for a right answer read as nonsense.
  keys: 'Answer',
  explanation: 'Explanation',
  visual: 'Characters & scene',
  ...ANSWER_LABELS,
};

/** A field on one scene, named the way both the diff and the undo button name it. */
export const sceneFieldName = (position: number, key: string): string =>
  `Scene ${String(position)} ${FIELD_NAMES[key] ?? key}`;

const fieldNamed = (patch: ScenePatch): string => {
  const [key] = Object.keys(patch);

  return key === undefined ? 'Scene' : (FIELD_NAMES[key] ?? key);
};

/**
 * The edit that undoes this one, named the way the reviewer would name it — GEN-43.
 *
 * Derived from the payload rather than passed in by the form. A screen that had to
 * remember to send its own previous value is a screen where the ninth field forgets, and a
 * wrong `before` is an undo that writes a value nobody ever typed.
 *
 * `undefined` when the row the write addresses is not in any lesson on the screen — a
 * scene removed under the reviewer, say. The save still goes; it just does not become
 * undoable, which is the honest outcome when the previous value cannot be read.
 */
export function undoStep(
  lessons: readonly LessonWorkspace[],
  write: FieldWrite,
): Step<FieldWrite> | undefined {
  const lesson = lessons.find((one) => one.id === write.lessonId);
  if (lesson === undefined) return undefined;

  if (write.kind === 'lesson') {
    /**
     * Every field the write carried, not the first one found — GEN-70's adversary.
     *
     * A planner proposal may set the title and the summary in one `PATCH`, and a step
     * that inverted only the title left the summary written and unundoable: the stack
     * emptied, and the console had no way back to a value it had just replaced.
     *
     * Personas and tags stay one axis, because the editor reports both together and
     * undoing a persona change with the tags half-applied is a state nobody asked for.
     */
    const body: Lesson['body'] = {};
    const named: string[] = [];

    if (write.body.title !== undefined) {
      body.title = lesson.title;
      named.push(FIELD_NAMES.title ?? 'Title');
    }

    if (write.body.summary !== undefined) {
      body.summary = lesson.summary;
      named.push(FIELD_NAMES.summary ?? 'Summary');
    }

    if (write.body.personas !== undefined || write.body.tags !== undefined) {
      body.personas = lesson.personas;
      body.tags = lesson.tags;
      named.push('Labels');
    }

    if (named.length === 0) return undefined;

    return {
      label: named.join(' and '),
      before: { ...write, body },
      after: write,
    };
  }

  if (write.kind === 'scene') {
    const scene = lesson.scenes.find((one) => one.id === write.sceneId);
    if (scene === undefined) return undefined;

    const body = inverseScene(scene, write.body);

    if (Object.keys(body).length === 0) return undefined;

    return {
      label: `Scene ${String(scene.position)} ${fieldNamed(write.body)}`,
      before: { ...write, body },
      after: write,
    };
  }

  const asked = lesson.scenes.find(
    (one) => 'questionId' in one && one.questionId === write.questionId,
  );

  if (asked === undefined || !('explanation' in asked)) return undefined;

  return {
    label: sceneFieldName(asked.position, 'explanation'),
    before: { ...write, explanation: asked.explanation ?? '' },
    after: write,
  };
}
